import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Plus, ArrowRight, MagnifyingGlass, Trash, Package, CalendarCheck } from "@phosphor-icons/react";
import { usePerms } from "@/lib/perms";

const ACCENT = "#10B981";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

export default function RecoleccionesList() {
  const { has } = usePerms();
  const canCreate = has("appointments:create");
  const canDelete = has("appointments:delete");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, hoy: 0, semana: 0, mes: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: s }] = await Promise.all([
        api.get("/recolecciones", { params: q ? { q } : {} }),
        api.get("/recolecciones/stats"),
      ]);
      setItems(data);
      setStats(s);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la recolección ${r.folio}?`)) return;
    try {
      await api.delete(`/recolecciones/${r.id}`);
      toast.success("Recolección eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] mb-2" style={{ color: ACCENT }}>
            Recolecciones · Pases
          </div>
          <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
            <Truck size={36} weight="duotone" style={{ color: ACCENT }} />
            Recolecciones
          </h1>
        </div>
        {canCreate && (
          <Link
            to="/recolecciones/nueva"
            data-testid="rec-new-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-sm text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] transition-transform"
            style={{ backgroundColor: ACCENT }}
          >
            <Plus size={16} /> Nueva Recolección
          </Link>
        )}
      </div>

      {/* Mini-dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total" value={stats.total} icon={Package} />
        <StatCard label="Hoy" value={stats.hoy} icon={CalendarCheck} />
        <StatCard label="Últimos 7 días" value={stats.semana} icon={CalendarCheck} />
        <StatCard label="Este mes" value={stats.mes} icon={CalendarCheck} />
      </div>

      <div className="bg-[#111111] border border-white/10 rounded-sm">
        <div className="p-4 border-b border-white/10 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="rec-search"
              placeholder="Buscar por folio…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 outline-none"
            />
          </div>
          <div className="ml-auto text-xs font-mono-data text-white/40">{items.length} registros</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3">Folio</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-left px-4 py-3">Cliente / Proveedor</th>
                <th className="text-left px-4 py-3">Persona</th>
                <th className="text-left px-4 py-3">Placas</th>
                <th className="text-right px-4 py-3">Pedidos</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="rec-rows">
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-white/50">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-white/50">Sin recolecciones aún.</td></tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 font-mono-data font-bold" style={{ color: ACCENT }}>{r.folio}</td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmt(r.appointment_datetime)}</td>
                    <td className="px-4 py-3 text-white/80">{r.cliente_name}</td>
                    <td className="px-4 py-3 text-white/70">{r.persona_recolecta || "—"}</td>
                    <td className="px-4 py-3 font-mono-data text-white/70">{r.placas || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-white">{(r.pedidos || []).length}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/recolecciones/${r.id}`}
                          data-testid="rec-open-btn"
                          className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest hover:text-white"
                          style={{ color: ACCENT }}
                        >
                          Abrir <ArrowRight size={10} />
                        </Link>
                        {canDelete && (
                          <button
                            onClick={() => remove(r)}
                            data-testid="rec-delete-btn"
                            className="p-1 rounded-sm text-white/40 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                          >
                            <Trash size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/40">{label}</div>
        <Icon size={14} style={{ color: ACCENT }} weight="duotone" />
      </div>
      <div className="font-mono-data text-3xl font-bold" style={{ color: ACCENT }}>{value}</div>
    </div>
  );
}
